// The runtime flag that decides which S-2 estimate provider answers: the deterministic heuristic, or
// Jev (TypeSafe AI's System One model) behind `/api/jev`.
//
// It lives here rather than in `src/state/store.ts` for one reason of sequencing: the store is owned by
// another work stream this week, and a toggle that cannot be built until that file is free is a toggle
// that does not get built. So the field's *shape* is declared here, with a module-level fallback that
// works today, and the store adopts it later with one line:
//
//     // src/sim/types.ts, in `interface Dials`:
//     /** S-2 estimate provider (src/ai/jevFlag.ts). Absent means the heuristic. */
//     jev?: { enabled: boolean };
//
// Once `dials.jev` exists, `getJevFlag(dials)` reads it and the module-level fallback is only consulted
// when the dials do not carry the field — which is exactly the behaviour a partial rollout wants.
//
// `?jev=1` in the query string forces it on regardless, for testing a deployed build without opening
// the instructor panel. `?jev=0` forces it off, which is what you want when a demo machine has the
// query string stuck in a bookmark.

/** The field this module proposes for `Dials`. Kept a named type so the store's eventual one line can
 * say `jev?: JevFlag` and there is one definition of it. */
export interface JevFlag {
  enabled: boolean;
}

/** What `getJevFlag` will accept: today a `Dials` without the field, tomorrow one with it. */
export interface DialsWithJev {
  jev?: JevFlag;
}

export const DEFAULT_JEV_FLAG: JevFlag = { enabled: false };

let moduleFlag: JevFlag = { ...DEFAULT_JEV_FLAG };
const listeners = new Set<() => void>();

/** `?jev=1` / `?jev=0`, or undefined when the query string says nothing. Read fresh each time rather
 * than cached, so a test can push a URL and a reader sees it. Safe under Node (no `location`). */
export function jevUrlOverride(search?: string): boolean | undefined {
  const raw = search ?? (typeof location !== 'undefined' ? location.search : '');
  if (!raw) return undefined;
  let value: string | null = null;
  try {
    value = new URLSearchParams(raw).get('jev');
  } catch {
    return undefined;
  }
  if (value === null) return undefined;
  if (value === '0' || value === 'false' || value === 'off') return false;
  return true;
}

/**
 * The effective flag.
 *
 * Precedence, highest first: the URL override, the dials (once the store carries the field), the
 * module-level value the toggle writes. The URL wins over the dials on purpose — it is the escape
 * hatch for a build whose instructor panel you cannot reach.
 */
export function getJevFlag(dials?: unknown, search?: string): JevFlag {
  const url = jevUrlOverride(search);
  if (url !== undefined) return { enabled: url };
  // `unknown`, not `DialsWithJev`: `Dials` does not carry the field yet, and a parameter typed as an
  // all-optional interface would make every `getEstimator(dials)` call site a type error today and a
  // silent no-op the day the field lands. Narrowed here instead, once.
  const candidate = (dials as DialsWithJev | null | undefined)?.jev;
  if (candidate && typeof candidate.enabled === 'boolean') return candidate;
  return moduleFlag;
}

/** Convenience: just the boolean. */
export function jevEnabled(dials?: unknown, search?: string): boolean {
  return getJevFlag(dials, search).enabled;
}

/**
 * Sets the module-level flag. The instructor toggle calls this today; once `dials.jev` exists the
 * toggle should write to the dials instead and this becomes the pre-store default only.
 */
export function setJevEnabled(enabled: boolean): void {
  if (moduleFlag.enabled === enabled) return;
  moduleFlag = { enabled };
  for (const fn of listeners) fn();
}

/** `useSyncExternalStore`-shaped subscription, so a chip re-renders when the flag moves. */
export function subscribeJevFlag(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The identity `useSyncExternalStore` compares: a new object only when the value actually changed. */
export function jevFlagSnapshot(): JevFlag {
  return moduleFlag;
}

/** Test seam: back to the shipped default with no listeners fired out of order. */
export function resetJevFlag(): void {
  moduleFlag = { ...DEFAULT_JEV_FLAG };
  for (const fn of listeners) fn();
}
